import { AbstractBuildstep } from "@project/emerge";
import type {
  DragConstraint,
  DragUpdateResult,
  SnapAnchor,
  SupplyPartDefinition,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import filter from "lodash/filter";
import * as THREE from "three";
import { AddableType } from "../enum/AddableType";
import { Alignment } from "../enum/Alignment";
import { Covers } from "../enum/Covers";
import { LTProducts } from "../enum/LTProducts";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { LTPartType } from "../enum/PartType";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";
import type { DishDefinition } from "../interfaces/DishProperties";
import type { DishType } from "../interfaces/DishType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { FootType } from "../interfaces/FootType";
import type { HandleDefinition } from "../interfaces/HandleProperties";
import type { HandleType } from "../interfaces/HandleType";
import type { Position } from "../interfaces/Position";
import type { TrolleyHandleDefinition } from "../interfaces/TrolleyHandleProperties";
import type { TrolleyHandleType } from "../interfaces/TrolleyHandleType";
import { compileAddableGrid } from "../structure/AddableInfos";
import type { Rect } from "../structure/Rect";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";

/**
 * Addable grid `relativeToBody`: false = full side / Case, true = corpus (or hood base) only.
 * UI may store values as boolean or string ("true"/"false"). Omitted → false (Case), matching legacy.
 */
export function parseAddableRelativeToBody(raw: unknown): boolean {
  return raw === true || raw === "true";
}

// --- Concrete Property Types (assuming FootProperties is similar) ---
// Need FootProperties definition if not already imported/available globally
// For now, creating a basic one based on HandleProperties
type FootDefinition = SupplyPartDefinition<LTPartType.Foot, FootType>;

// --- Output Union Type ---
type AddablePartProperties =
  | DishDefinition
  | HandleDefinition
  | TrolleyHandleDefinition
  | FootDefinition; // Add other types like Runner if needed later

// --- Dependency Data Interfaces ---
interface BaseAddableStepDependency {
  partId: string;
  groupName: string;
  buildstepName: string;
  isSupplyPart: true;
  position: THREE.Vector3; // Final calculated position
  // Drag constraint data
  sourceOptionUuid: string;
  rectBoundsMin: THREE.Vector3;
  rectBoundsMax: THREE.Vector3;
  rectNormal: THREE.Vector3;
  rectCenter: THREE.Vector3;
  // Logical bounds for calculation (may differ from visual bounds)
  logicalRectBoundsMin: THREE.Vector3;
  logicalRectBoundsMax: THREE.Vector3;
  logicalRectCenter: THREE.Vector3;
  // Positioning context
  horizontalCount: number;
  verticalCount: number;
  horizontalN: number;
  verticalN: number;
  horizontalAlignment: Alignment;
  verticalAlignment: Alignment;
  // Angle section padding (to prevent overlap)
  angleSectionDepth: number;
}

interface AddableDishDependencyData extends BaseAddableStepDependency {
  partKind: "AddableDish";
  type: LTPartType.Dish;
  partSelection: DishType;
  orientation: number; // degrees
  side: Sides;
  flip: boolean;
}

interface AddableHandleDependencyData extends BaseAddableStepDependency {
  partKind: "AddableHandle";
  type: LTPartType.Handle;
  partSelection: HandleType;
  orientation: number; // degrees
  side: Sides;
}

interface AddableTrolleyHandleDependencyData extends BaseAddableStepDependency {
  partKind: "AddableTrolleyHandle";
  type: LTPartType.TrolleyHandle;
  partSelection: TrolleyHandleType;
  orientation: number; // degrees
  side: Sides;
}

interface AddableFootDependencyData extends BaseAddableStepDependency {
  partKind: "AddableFoot";
  type: LTPartType.Foot;
  partSelection: FootType;
  // Optional side/orientation if needed for Feet
  side?: Sides;
  orientation?: number;
}

type AddableStepDependencyData =
  | AddableDishDependencyData
  | AddableHandleDependencyData
  | AddableTrolleyHandleDependencyData
  | AddableFootDependencyData;

// --- Helper ---
function getCut(
  orientation: number,
  orientationOnRect: OrientationOnRect,
  cutX?: number,
  cutY?: number,
): number | undefined {
  const a = orientation % 180 !== 0;
  const b = orientationOnRect === OrientationOnRect.HORIZONTAL;

  if (a !== b) {
    return cutX ? cutX / 2 : undefined;
  } else {
    return cutY ? cutY / 2 : undefined;
  }
}

// Map of addable types to part types (defined outside class)
const addableType2PartType: Record<AddableType, LTPartType | null> = {
  [AddableType.Dish]: LTPartType.Dish,
  [AddableType.Foot]: LTPartType.Foot,
  [AddableType.Handle]: LTPartType.Handle,
  [AddableType.Runner]: LTPartType.Roller, // Map Runner to Roller part type if correct? Double check this mapping.
  [AddableType.TrolleyHandle]: LTPartType.TrolleyHandle,
};

// --- Refactored Build Step ---
export class AddableBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AddablePartProperties,
  AddableStepDependencyData
> {
  constructor() {
    super("AddableBuildStep");
  }

  findRectsForSide(blueprint: FlightCaseBlueprint, side: Sides): Rect[] {
    return blueprint.rects.filter((rect) => rect.side === side);
  }

  /**
   * Legacy AddableBuilder: Relativ zum Korpus → only rects in the corpus group (or hood base);
   * Relativ zum Case → all rects on that side (covers + corpus), so positioning uses full-case edges.
   */
  findRectsForAddable(
    blueprint: FlightCaseBlueprint,
    side: Sides,
    relativeToBody: boolean,
    productType: string,
  ): Rect[] {
    const onSide = blueprint.rects.filter((rect) => rect.side === side);
    if (!relativeToBody) {
      return onSide;
    }
    const isHood =
      productType === LTProducts.Haube ||
      productType === LTProducts.KeyboardCase;
    const targetGroup = isHood ? Covers.BASE : DefaultGroupName;
    return onSide.filter((rect) => rect.group === targetGroup);
  }

  // Helper: resolve position on rects
  private resolvePositionOnRects(
    rects: Rect[],
    horizontalPosition: Position,
    verticalPosition: Position,
    orientation: number,
    cutX?: number,
    cutY?: number,
    relativeToBase?: boolean,
  ): { rect: Rect; x: number; y: number } | null {
    for (const rect of rects) {
      const testX = rect.resolvePosition1d(
        horizontalPosition,
        OrientationOnRect.HORIZONTAL,
        getCut(orientation, OrientationOnRect.HORIZONTAL, cutX, cutY),
        !relativeToBase,
      );
      const testY = rect.resolvePosition1d(
        verticalPosition,
        OrientationOnRect.VERTICAL,
        getCut(orientation, OrientationOnRect.VERTICAL, cutX, cutY),
        !relativeToBase,
      );

      if (testX !== null && testY !== null) {
        return { rect, x: testX, y: testY };
      }
    }
    return null;
  }

  /**
   * Old `AddableBuilder`: `horizontalDir*x + verticalDir*y`, `position[normal] = outerCenter[normal]`.
   */
  private static composeAddablePositionVectorDirs(
    rect: Rect,
    resolvedHorizontal: number,
    resolvedVertical: number,
  ): THREE.Vector3 {
    const { horizontalDir, verticalDir, normal } = rect.properties;
    const position = new THREE.Vector3();
    position.add(horizontalDir.clone().multiplyScalar(resolvedHorizontal));
    position.add(verticalDir.clone().multiplyScalar(resolvedVertical));
    position[normal] = rect.outerCenter[normal];
    return position;
  }

  private calculatePosition(rect: Rect, x: number, y: number): THREE.Vector3 {
    return AddableBuildStep.composeAddablePositionVectorDirs(rect, x, y);
  }

  // Helper: create dependency for specific addable type
  private createAddableDependency(
    addableType: AddableType,
    partType: LTPartType,
    partSelection: DishType | HandleType | TrolleyHandleType | FootType,
    orientation: number,
    side: Sides,
    baseDep: BaseAddableStepDependency,
  ): AddableStepDependencyData | null {
    switch (addableType) {
      case AddableType.Dish: {
        let flip = false;
        const maybeFlip = (partSelection as { flip?: boolean }).flip;
        if (typeof maybeFlip === "boolean") flip = maybeFlip;
        return {
          ...baseDep,
          partKind: "AddableDish",
          type: partType as LTPartType.Dish,
          partSelection: partSelection as DishType,
          orientation,
          side,
          flip,
        };
      }
      case AddableType.Handle:
        return {
          ...baseDep,
          partKind: "AddableHandle",
          type: partType as LTPartType.Handle,
          partSelection: partSelection as HandleType,
          orientation,
          side,
        };
      case AddableType.TrolleyHandle:
        return {
          ...baseDep,
          partKind: "AddableTrolleyHandle",
          type: partType as LTPartType.TrolleyHandle,
          partSelection: partSelection as TrolleyHandleType,
          orientation,
          side,
        };
      case AddableType.Foot:
        return {
          ...baseDep,
          partKind: "AddableFoot",
          type: partType as LTPartType.Foot,
          partSelection: partSelection as FootType,
          side: side,
          orientation: orientation,
        };
      case AddableType.Runner:
        console.warn(
          "AddableType.Runner encountered, needs specific handling if mapped to Roller.",
        );
        return null;
      default:
        return null;
    }
  }

  // Helper: compute rect bounds for drag constraints
  private computeRectBounds(rect: Rect): {
    min: THREE.Vector3;
    max: THREE.Vector3;
    center: THREE.Vector3;
  } {
    // Get the inner corners of the rect (actual panel surface, not outer edge)
    const corners = rect.corners.map((c) => c.innerPos);
    const min = new THREE.Vector3(
      Math.min(...corners.map((c) => c.x)),
      Math.min(...corners.map((c) => c.y)),
      Math.min(...corners.map((c) => c.z)),
    );
    const max = new THREE.Vector3(
      Math.max(...corners.map((c) => c.x)),
      Math.max(...corners.map((c) => c.y)),
      Math.max(...corners.map((c) => c.z)),
    );
    return { min, max, center: rect.innerCenter };
  }

  // Helper: compute logical rect bounds for positioning calculation
  private computeLogicalRectBounds(
    rect: Rect,
    overFullcase: boolean,
  ): {
    min: THREE.Vector3;
    max: THREE.Vector3;
    center: THREE.Vector3;
  } {
    const points: THREE.Vector3[] = [];

    // Rect edges define the boundary
    // Use edges directly to match resolvePosition1d logic source
    for (const edge of rect.edges) {
      if (!edge) continue;
      if (overFullcase) {
        points.push(edge.start.originalOuterPos);
        points.push(edge.end.originalOuterPos);
      } else {
        points.push(edge.start.outerPos);
        points.push(edge.end.outerPos);
      }
    }

    // Fallback if edges missing
    if (points.length === 0) {
      return this.computeRectBounds(rect);
    }

    const min = new THREE.Vector3(
      Math.min(...points.map((p) => p.x)),
      Math.min(...points.map((p) => p.y)),
      Math.min(...points.map((p) => p.z)),
    );
    const max = new THREE.Vector3(
      Math.max(...points.map((p) => p.x)),
      Math.max(...points.map((p) => p.y)),
      Math.max(...points.map((p) => p.z)),
    );
    const center = min.clone().add(max).multiplyScalar(0.5);

    return { min, max, center };
  }

  // Helper: process single addable
  private processAddable(
    addable: AddableGridInfos,
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    /** Index among `filter(variableOptions, addableType)` — legacy rows can share `uuid`. */
    addableSlotIndex: number,
    angleSectionDepth: number,
  ): AddableStepDependencyData[] {
    const dependencies: AddableStepDependencyData[] = [];
    const addableType = addable.addableType;
    const partType = addableType2PartType[addableType];

    if (!partType) {
      console.warn(
        `AddableBuildStep: Unsupported addableType ${addableType}. Skipping.`,
      );
      return dependencies;
    }

    const addablePositions = compileAddableGrid(addable);
    const relativeToBase = parseAddableRelativeToBody(addable.relativeToBody);

    for (const gridElement of addablePositions) {
      const {
        partSelection,
        horizontalPosition,
        verticalPosition,
        orientation = 0,
        side,
        sourceOptionUuid,
        gridIndexH,
        gridIndexV,
      } = gridElement;

      if (!partSelection) continue;

      const { cutX, cutY } =
        (partSelection as { cutX?: number; cutY?: number }) ?? {};
      const rects = this.findRectsForAddable(
        blueprint,
        side,
        relativeToBase,
        settings.type,
      );
      if (rects.length === 0) {
        console.warn(
          `AddableBuildStep: Could not find any rect for side ${side}. Skipping addable.`,
        );
        continue;
      }

      const resolved = this.resolvePositionOnRects(
        rects,
        horizontalPosition,
        verticalPosition,
        orientation,
        cutX,
        cutY,
        relativeToBase,
      );

      if (!resolved) {
        console.warn(
          `AddableBuildStep: Could not resolve 1D position for addable on side ${side} for any rect. Skipping.`,
        );
        continue;
      }

      const { rect, x, y } = resolved;

      const position = this.calculatePosition(rect, x, y);
      // Stable ID: slot disambiguates duplicate option uuids (e.g. two HANDLES rows, order 23629).
      const partId = `addable_${sourceOptionUuid}_s${addableSlotIndex}_${side}_h${gridIndexH}_v${gridIndexV}`;

      // Compute rect bounds for drag constraint
      const rectBounds = this.computeRectBounds(rect);
      // Compute logical bounds for calculation (matching overFullcase logic in resolvePosition1d)
      const overFullcase = !relativeToBase;
      const logicalBounds = this.computeLogicalRectBounds(rect, overFullcase);

      const rectProps = RectPropertiesBySide[side];
      const rectNormal = rectProps?.normalDir.clone() ?? new THREE.Vector3();

      const baseDep: BaseAddableStepDependency = {
        partId,
        groupName: rect.group,
        buildstepName: this.buildstepName,
        isSupplyPart: true as const,
        position: position.clone(),
        // Drag constraint data
        sourceOptionUuid,
        rectBoundsMin: rectBounds.min,
        rectBoundsMax: rectBounds.max,
        rectNormal,
        rectCenter: rectBounds.center,
        // Logical bounds
        logicalRectBoundsMin: logicalBounds.min,
        logicalRectBoundsMax: logicalBounds.max,
        logicalRectCenter: logicalBounds.center,
        // Positioning context
        horizontalCount: addable.horizontalCount ?? 1,
        verticalCount: addable.verticalCount ?? 1,
        // Ensure n values are valid numbers (fallback to 0.5 for center if NaN)
        horizontalN: Number.isNaN(horizontalPosition.n)
          ? 0.5
          : horizontalPosition.n,
        verticalN: Number.isNaN(verticalPosition.n) ? 0.5 : verticalPosition.n,
        horizontalAlignment: horizontalPosition.alignment,
        verticalAlignment: verticalPosition.alignment,
        // Angle section padding
        angleSectionDepth,
      };

      const dep = this.createAddableDependency(
        addableType,
        partType,
        partSelection as DishType | HandleType | TrolleyHandleType | FootType,
        orientation,
        side,
        baseDep,
      );

      if (dep) {
        dependencies.push(dep);
      }
    }

    return dependencies;
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, AddableStepDependencyData> {
    const dependencies = new Map<string, AddableStepDependencyData>();

    // Get angle section depth for constraint padding
    const angleSectionDepth =
      settings.exteriorOptions.angleSections?.depth ?? 0;

    // Filter variable options to find addables
    const addables = filter(settings.variableOptions, "addableType");
    let addableSlotIndex = 0;

    for (const addable of addables) {
      switch (addable.variableOptionType) {
        case VariableOptionType.DISHES:
        case VariableOptionType.TROLLEY_HANDLES:
        case VariableOptionType.HANDLES:
        case VariableOptionType.FEET: {
          const deps = this.processAddable(
            addable,
            blueprint,
            settings,
            addableSlotIndex++,
            angleSectionDepth,
          );
          deps.forEach((dep: AddableStepDependencyData) => {
            dependencies.set(dep.partId, dep);
          });
          break;
        }
      }
    }

    return dependencies;
  }

  // Helper: build drag constraint for surface movement
  private buildSurfaceDragConstraint(
    dependencies: BaseAddableStepDependency,
    side: Sides,
    partCutX?: number,
    partCutY?: number,
    orientation?: number,
  ): DragConstraint {
    const {
      rectBoundsMin,
      rectBoundsMax,
      rectNormal,
      rectCenter,
      sourceOptionUuid,
      angleSectionDepth,
    } = dependencies;

    // Calculate total padding: angle section depth + half of part's own size
    // Part size depends on orientation (cutX/cutY may swap based on rotation)
    const orientedCutX =
      orientation && orientation % 180 !== 0
        ? (partCutY ?? 0)
        : (partCutX ?? 0);
    const orientedCutY =
      orientation && orientation % 180 !== 0
        ? (partCutX ?? 0)
        : (partCutY ?? 0);

    const horizontalPadding = angleSectionDepth + orientedCutX / 2;
    const verticalPadding = angleSectionDepth + orientedCutY / 2;

    const rectProps = RectPropertiesBySide[side];
    // Get which axis is horizontal/vertical for this side (e.g., "x", "y", "z")
    const horizontalAxis = rectProps?.horizontal ?? "x";
    const verticalAxis = rectProps?.vertical ?? "y";

    // Shrink bounds inward by padding - always move min up and max down on each axis
    const paddedMin = rectBoundsMin.clone();
    const paddedMax = rectBoundsMax.clone();

    // For horizontal axis: if min < max, add padding to min and subtract from max
    if (paddedMin[horizontalAxis] < paddedMax[horizontalAxis]) {
      paddedMin[horizontalAxis] += horizontalPadding;
      paddedMax[horizontalAxis] -= horizontalPadding;
    } else {
      paddedMin[horizontalAxis] -= horizontalPadding;
      paddedMax[horizontalAxis] += horizontalPadding;
    }

    // For vertical axis: if min < max, add padding to min and subtract from max
    if (paddedMin[verticalAxis] < paddedMax[verticalAxis]) {
      paddedMin[verticalAxis] += verticalPadding;
      paddedMax[verticalAxis] -= verticalPadding;
    } else {
      paddedMin[verticalAxis] -= verticalPadding;
      paddedMax[verticalAxis] += verticalPadding;
    }

    // Build snap anchors - center of surface
    const snapAnchors: SnapAnchor[] = [
      {
        position: [rectCenter.x, rectCenter.y, rectCenter.z],
        label: "Center",
      },
    ];

    return {
      type: "surface",
      planeNormal: [rectNormal.x, rectNormal.y, rectNormal.z],
      bounds: {
        min: [paddedMin.x, paddedMin.y, paddedMin.z],
        max: [paddedMax.x, paddedMax.y, paddedMax.z],
      },
      snapAnchors,
      sourceOptionUuid,
    };
  }

  // Helper: build settings context for drag updates
  private buildSettingsContext(
    dependencies: BaseAddableStepDependency,
    side: Sides,
  ): Record<string, unknown> {
    const {
      rectBoundsMin,
      rectBoundsMax,
      rectNormal,
      rectCenter,
      logicalRectBoundsMin,
      logicalRectBoundsMax,
      logicalRectCenter,
      horizontalCount,
      verticalCount,
      horizontalN,
      verticalN,
      horizontalAlignment,
      verticalAlignment,
      angleSectionDepth,
    } = dependencies;
    return {
      side,
      rectBoundsMin: [rectBoundsMin.x, rectBoundsMin.y, rectBoundsMin.z],
      rectBoundsMax: [rectBoundsMax.x, rectBoundsMax.y, rectBoundsMax.z],
      rectNormal: [rectNormal.x, rectNormal.y, rectNormal.z],
      rectCenter: [rectCenter.x, rectCenter.y, rectCenter.z],
      logicalRectBoundsMin: [
        logicalRectBoundsMin.x,
        logicalRectBoundsMin.y,
        logicalRectBoundsMin.z,
      ],
      logicalRectBoundsMax: [
        logicalRectBoundsMax.x,
        logicalRectBoundsMax.y,
        logicalRectBoundsMax.z,
      ],
      logicalRectCenter: [
        logicalRectCenter.x,
        logicalRectCenter.y,
        logicalRectCenter.z,
      ],
      horizontalCount,
      verticalCount,
      horizontalN,
      verticalN,
      horizontalAlignment,
      verticalAlignment,
      angleSectionDepth,
    };
  }

  public createPart(
    partId: string,
    dependencies: AddableStepDependencyData,
  ): AddablePartProperties {
    // Common logic: calculate rotation if applicable
    let finalRotation: THREE.Euler | undefined;
    if (
      "side" in dependencies &&
      typeof dependencies.side === "number" &&
      "orientation" in dependencies &&
      typeof dependencies.orientation === "number"
    ) {
      finalRotation = this.rotateOntoSide(
        dependencies.side,
        dependencies.orientation,
      );
    } else {
      // Default rotation for parts without explicit side/orientation (e.g., Foot)
      finalRotation = new THREE.Euler(0, 0, 0);
    }

    // Extract part size from partSelection for padding calculation
    const partSelection = dependencies.partSelection as {
      cutX?: number;
      cutY?: number;
    };
    const partCutX = partSelection?.cutX;
    const partCutY = partSelection?.cutY;
    const orientation =
      "orientation" in dependencies ? dependencies.orientation : 0;

    // Return specific type based on partKind
    switch (dependencies.partKind) {
      case "AddableDish":
        return {
          // Base SupplyPart props from dependencies
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation,
          side: dependencies.side,
          flip: dependencies.flip,
          // Drag constraint for surface movement
          dragConstraint: this.buildSurfaceDragConstraint(
            dependencies,
            dependencies.side,
            partCutX,
            partCutY,
            orientation,
          ),
          settingsContext: this.buildSettingsContext(
            dependencies,
            dependencies.side,
          ),
        } as DishDefinition;
      case "AddableHandle":
        return {
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation,
          side: dependencies.side,
          // Drag constraint for surface movement
          dragConstraint: this.buildSurfaceDragConstraint(
            dependencies,
            dependencies.side,
            partCutX,
            partCutY,
            orientation,
          ),
          settingsContext: this.buildSettingsContext(
            dependencies,
            dependencies.side,
          ),
        } as HandleDefinition;
      case "AddableTrolleyHandle":
        return {
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation,
          side: dependencies.side,
          // Drag constraint for surface movement
          dragConstraint: this.buildSurfaceDragConstraint(
            dependencies,
            dependencies.side,
            partCutX,
            partCutY,
            orientation,
          ),
          settingsContext: this.buildSettingsContext(
            dependencies,
            dependencies.side,
          ),
        } as TrolleyHandleDefinition;
      case "AddableFoot":
        return {
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation ?? 0,
          side: dependencies.side,
          // Drag constraint for surface movement (if side is defined)
          ...(dependencies.side !== undefined
            ? {
                dragConstraint: this.buildSurfaceDragConstraint(
                  dependencies,
                  dependencies.side,
                  partCutX,
                  partCutY,
                  orientation,
                ),
                settingsContext: this.buildSettingsContext(
                  dependencies,
                  dependencies.side,
                ),
              }
            : {}),
        } as FootDefinition;
      default: {
        const exhaustiveCheck: never = dependencies;
        throw new Error(`Unknown addable part kind: ${exhaustiveCheck}`);
      }
    }
  }

  /**
   * Convert a drag position change back to settings updates.
   * For addables on surfaces, this updates the horizontal/vertical margins.
   * Returns both the settings update and display values for UI feedback.
   */
  public updateSettingsFromDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    if (!constraint.sourceOptionUuid || !settingsContext) {
      return null;
    }

    // Find the variable option by UUID
    const optionIndex = currentSettings.variableOptions.findIndex(
      (opt) => opt.uuid === constraint.sourceOptionUuid,
    );

    if (optionIndex === -1) {
      console.warn(
        `Could not find variableOption with UUID ${constraint.sourceOptionUuid}`,
      );
      return null;
    }

    const option = currentSettings.variableOptions[
      optionIndex
    ] as unknown as AddableGridInfos;

    // Extract rect info from settings context
    const side = settingsContext.side as Sides;

    // Original visual bounds (still extracted but unused for calculation now)
    // const rectCenter = ...
    // const rectBoundsMin = ...
    // const rectBoundsMax = ...

    const logicalRectBoundsMin = new THREE.Vector3(
      ...(settingsContext.logicalRectBoundsMin as [number, number, number]),
    );
    const logicalRectBoundsMax = new THREE.Vector3(
      ...(settingsContext.logicalRectBoundsMax as [number, number, number]),
    );
    const logicalRectCenter = new THREE.Vector3(
      ...(settingsContext.logicalRectCenter as [number, number, number]),
    );

    // Context from dependencies
    const horizontalCount = (settingsContext.horizontalCount as number) ?? 1;
    const verticalCount = (settingsContext.verticalCount as number) ?? 1;
    const horizontalN = (settingsContext.horizontalN as number) ?? 0;
    const verticalN = (settingsContext.verticalN as number) ?? 0;
    const horizontalAlignmentCtx =
      (settingsContext.horizontalAlignment as Alignment) ?? Alignment.CENTER;
    const verticalAlignmentCtx =
      (settingsContext.verticalAlignment as Alignment) ?? Alignment.CENTER;

    // Get rect properties for this side
    const rectProps = RectPropertiesBySide[side];
    if (!rectProps) {
      console.warn(`No rect properties for side ${side}`);
      return null;
    }

    const { horizontalDir, verticalDir } = rectProps;

    // Calculate the size of the logical rect (used for positioning)
    const rectSize = logicalRectBoundsMax.clone().sub(logicalRectBoundsMin);
    const horizontalSize = Math.abs(rectSize.dot(horizontalDir));
    const verticalSize = Math.abs(rectSize.dot(verticalDir));

    // Calculate offset from logical center
    const offsetFromCenter = newPosition.clone().sub(logicalRectCenter);

    // Get the offset along each axis. We use the dot product with the direction vector.
    // However, resolvePosition1d uses dir[coord] which can be -1 for some sides.
    // The formula in resolvePosition1d is: p = startPos + (margin + offset + padding) * dir[coord]
    //
    // For CENTER alignment with count=1, the margin IS the offset from center.
    // For START alignment (count > 1), we need to invert the calculation.
    //
    // The dot product already accounts for the direction, so we use it directly.
    // But we need to ensure consistency with how resolvePosition1d interprets margin.
    let horizontalOffset = offsetFromCenter.dot(horizontalDir);
    const orient = option.orientation ?? 0;
    if (
      rectProps.horizontal === "z" &&
      side === Sides.LEFT &&
      orient % 180 !== 0
    ) {
      horizontalOffset = -horizontalOffset;
    }
    const verticalOffset = offsetFromCenter.dot(verticalDir);

    // Get cut dimensions for padding
    const rawSelection = option.partSelection as {
      cutX?: number;
      cutY?: number;
    };
    const { cutX, cutY } = rawSelection ?? {};
    const paddingX =
      getCut(option.orientation, OrientationOnRect.HORIZONTAL, cutX, cutY) || 0;
    const paddingY =
      getCut(option.orientation, OrientationOnRect.VERTICAL, cutX, cutY) || 0;

    // Calculate new margins
    // Helper to calculate margin based on position, count, and alignment
    const calculateMargin = (
      offset: number, // Distance from Center (signed, in direction of horizontalDir/verticalDir)
      size: number,
      count: number,
      n: number, // Interpolation factor (0..1)
      padding: number,
      alignmentCtx: Alignment, // Alignment from context (START/CENTER/END)
    ): number => {
      // Current position from Start (0) to End (size)
      // offset is from Center, so:
      // pos = offset + size / 2
      const pos = offset + size / 2;

      // Logic for count > 1 or Alignment.START/END
      // Formula: m = (pos - Ln - p(1-2n)) / (1-2n)
      if (count > 1 || alignmentCtx !== Alignment.CENTER) {
        // Use n to determine logic
        const denom = 1 - 2 * n;
        if (Math.abs(denom) < 0.001) {
          // n is 0.5 (Center) - cannot adjust margin by dragging center element
          return 0; // Or keep existing margin?
        }

        const newMargin = (pos - size * n - padding * denom) / denom;
        return Math.max(0, newMargin); // Clamp to positive
      } else {
        // CENTER alignment (count=1)
        // Margin is offset from center
        return offset;
      }
    };

    const newHorizontalMargin = calculateMargin(
      horizontalOffset,
      horizontalSize,
      horizontalCount,
      horizontalN,
      paddingX,
      horizontalAlignmentCtx,
    );

    const newVerticalMargin = calculateMargin(
      verticalOffset,
      verticalSize,
      verticalCount,
      verticalN,
      paddingY,
      verticalAlignmentCtx,
    );

    // Round the margin values
    const roundedHorizontalMargin = Math.round(newHorizontalMargin);
    const roundedVerticalMargin = Math.round(newVerticalMargin);

    // Create updated variable options array
    const updatedVariableOptions = [...currentSettings.variableOptions];
    updatedVariableOptions[optionIndex] = {
      ...option,
      horizontalMargin: roundedHorizontalMargin,
      verticalMargin: roundedVerticalMargin,
    };

    return {
      settingsUpdate: {
        variableOptions: updatedVariableOptions,
      },
      displayValues: {
        primary: {
          label: "H",
          value: roundedHorizontalMargin,
          unit: "mm",
        },
        secondary: {
          label: "V",
          value: roundedVerticalMargin,
          unit: "mm",
        },
      },
    };
  }
}
